import { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../../../components/form/Search";
import Button from "../../../../components/form/Button";
import Table from "../../../../components/elements/table/Table";
import { getPaginationRange, getSortQuery, handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { userDelete, userList, userStatusChange } from "../../../../services/userService";
import Badge from "../../../../components/elements/Badge";
import { classNames } from "../../../../helpers/classNames";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ManageUserDrawer from "../../../../components/sections/admin/user/drawer/ManageUserDrawer";
import { useTranslation } from "react-i18next";
import ActionBar from "../../../../components/elements/ActionBar";
import Tooltip from "../../../elements/Tooltip";
import { email_verified, status_filter } from "../../../constant/constant";
import TableControls from "../../../elements/TableControls";

const List = () => {
  const { t } = useTranslation();
  const { session, loginData } = useSelector((state) => state?.authProvider);
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ["user", filters, search],
    queryFn: async () => {
      const { role = [], statusCode = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        ...Object.fromEntries([...role].map((val, i) => [`role[${i}]`, val])),
        ...Object.fromEntries([...statusCode].map((val, i) => [`statusCode[${i}]`, val])),
      };

      const res = await userList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  const options = [
    { id: "email", icon: "fa-regular fa-envelope", tooltip: "Resend Email" },
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  // add or edit user =================>
  function userManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["user", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [res?.data, ...stateData];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["user", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["user", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   User status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["user", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                status: item.status === "active" ? "inactive" : "active",
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await userStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("User status change successfully.");
      } else {
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  status: item.status === "active" ? "inactive" : "active",
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Name",
      align: "left",
      isSort: true,
      onSort: (val) => {
        setFilters((pre) => ({
          ...pre,
          sortQuery: getSortQuery("firstName", val),
        }));
      },
    },
    {
      _id: 2,
      name: "Email",
      align: "left",
      isSort: true,
      onSort: (val) => {
        setFilters((pre) => ({
          ...pre,
          sortQuery: getSortQuery("email", val),
        }));
      },
      isFilter: false,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            emailVerified: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.emailVerified;
            return { ...pre };
          });
        }
      },
      filterData: email_verified || [],
    },
    {
      _id: 3,
      name: "Role",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            role: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.role;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "User", value: "user", type: "checkbox" },
        { name: "Moderator", value: "moderator", type: "checkbox" },
        { name: "Admin", value: "admin", type: "checkbox" },
        { name: "Super Admin", value: "superadmin", type: "checkbox" },
      ],
    },
    {
      _id: "phone",
      name: "Phone",
      align: "left",
    },

    {
      _id: 4,
      name: "Last Login",
      align: "left",
      isSort: false,
      onSort: (val) => {
        setFilters((pre) => ({
          ...pre,
          sortQuery: getSortQuery("lastLogin", val),
        }));
      },
    },
    {
      _id: "banned",
      name: "Banned",
      align: "left",
      isFilter: false,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            isBanned: val?.map((item) => item?.isBanned?.isBanned),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.isBanned;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Banned User", value: true, type: "checkbox" },
        { name: "Regular User", value: false, type: "checkbox" },
      ],
    },
    {
      _id: "status",
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    {
      _id: 7,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: classNames(item?.firstName, item?.lastName) || "-",
          image: item?.image?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Tooltip
              position="bottom"
              className=""
              message={
                <div className="text-[12px] whitespace-pre-wrap max-w-xs">
                  {item?.emailVerified === 1
                    ? "This user's email is verified.\nClick to send an email."
                    : "This user's email is not verified.\nClick to resend a verification invitation."}
                </div>
              }>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-gray-500"></i>
                {item?.email ? (
                  <>
                    {item?.emailVerified === 1 ? (
                      <a href={`mailto:${item.email}`} className="hover:text-indigo-700 text-gray-800">
                        {item.email}
                      </a>
                    ) : (
                      <button
                        // onClick={() => handleResendVerification(item.email)}
                        className=" hover:text-rose-600 transition">
                        {item.email}
                      </button>
                    )}

                    {item?.emailVerified === 1 ? (
                      <i className="fa-solid fa-shield-check text-green-500" title="Verified"></i>
                    ) : (
                      <i className="fa-solid fa-triangle-exclamation text-rose-500" title="Not Verified"></i>
                    )}
                  </>
                ) : (
                  "-"
                )}
              </p>
            </Tooltip>
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Badge
                type={item?.role === "admin" ? "info" : "danger"}
                label={item?.role === "admin" ? "ADMIN" : "USER"}
                customColor={
                  item?.role === "admin"
                    ? "bg-gradient-to-r from-yellow-700 via-yellow-600 to-yellow-600 text-white border border-yellow-200"
                    : "bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400 text-white border border-indigo-200"
                }
              />
            </div>
          ),
        },
        {
          _id: "phone",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-gray-500"></i>
                <a
                  href={`tel:+${(item?.phone?.countryCode || "").replace("+", "")}${item?.phone?.number || ""}`}
                  className=" text-gray-800 hover:text-blue-700">
                  +{(item?.phone?.countryCode || "").replace("+", "")} {item?.phone?.number || ""}
                </a>
              </p>
            </>
          ),
        },

        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Tooltip
              position="right"
              message={
                <div className="text-[12px] whitespace-pre-wrap max-w-xs">
                  {item?.lastLogin
                    ? `This user last logged in on ${dayjs(item.lastLogin).format("DD MMMM, YYYY")} at ${dayjs(
                        item.lastLogin
                      ).format("hh:mm A")}.`
                    : "No login activity found for this user."}
                </div>
              }>
              {item?.lastLogin ? dayjs(item.lastLogin).format("DD MMM, YYYY • hh:mm A") : "No login activity"}
            </Tooltip>
          ),
        },
        // {
        //   _id: "loginMethod",
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.mainLogin || "-",
        // },
        {
          _id: "banned",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex items-center">
              <Tooltip
                position="top"
                message={
                  <div className="text-[12px] max-w-xs whitespace-pre-wrap">
                    {item?.isBanned?.isBanned
                      ? "This user is currently banned from the platform and cannot access their account."
                      : "This user is in good standing and has full access to the platform."}
                  </div>
                }>
                <div className="flex items-center gap-1 text-sm">
                  <i
                    className={`fa-solid ${
                      item?.isBanned?.isBanned ? "fa-user-slash text-rose-500" : "fa-user text-green-500"
                    } text-[16px]`}></i>
                  <span className={`${item?.isBanned?.isBanned ? "text-rose-500" : "text-green-500"} font-medium`}>
                    {item?.isBanned?.isBanned ? "Banned" : "Regular"}
                  </span>
                </div>
              </Tooltip>
            </div>
          ),
        },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}>
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status === "active" ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            // {
            //   _id: 1,
            //   name: "Reset Passwoord",
            //   icon: "fa-regular fa-key",
            //   onClick: () => {
            //     // handleResetPassword(item);
            //   },
            //   standout: false,
            // },
            {
              _id: 2,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: 3,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await userDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["user", filters, search]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="flex items-center gap-3">
        <TableControls
          limit={filters?.limit}
          totalDocs={data?.metadata?.totalDocs}
          onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
          onSearch={(val) => {
            setKeyword(val);
            handleFilterChange("page", 1, setFilters);
          }}
        />
        {selectedItems?.length > 0 && (
          <div className="flex items-center gap-2">
            <ActionBar options={options} onChange={actionHandler} />
            <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
              {selectedItems?.length} Selected
              <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                <i className="fa-fw fa-light fa-xmark"></i>
              </button>
            </span>
          </div>
        )}
        <div className="relative ml-auto">
          <Button
            buttonIcon={"fa-regular fa-plus"}
            buttonLabel={"Create New"}
            buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
          />
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        selectable
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
      <ManageUserDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={userManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default List;
